import * as Blockly from 'blockly/core'
import { LuaGenerator, Order } from 'blockly/lua'
import {
  binaryBooleanOperator,
  binaryNumberOperator,
  binaryOperator,
  BlockDefinition,
  booleanOutput,
  equalityOperator,
  numberOutput,
  relationalOperator,
} from './common'

export const operatorDefaults: Partial<BlockDefinition> = {
  style: 'operators',
}

export const operatorBlocks: Array<BlockDefinition> = [
  {
    opcode: 'operator_add',
    ...binaryNumberOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.ADDITIVE)
      const v2 = generator.valueToCode(block, 'VAL2', Order.ADDITIVE)
      return [`${v} + ${v2}`, Order.ADDITIVE]
    },
    message0: '%1 + %2',
  },
  {
    opcode: 'operator_subtract',
    ...binaryNumberOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.ADDITIVE)
      const v2 = generator.valueToCode(block, 'VAL2', Order.ADDITIVE)
      return [`${v} - ${v2}`, Order.ADDITIVE]
    },
    message0: '%1 - %2',
  },
  {
    opcode: 'operator_multiply',
    ...binaryNumberOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.MULTIPLICATIVE)
      const v2 = generator.valueToCode(block, 'VAL2', Order.MULTIPLICATIVE)
      return [`${v} * ${v2}`, Order.MULTIPLICATIVE]
    },
    message0: '%1 * %2',
  },
  {
    opcode: 'operator_divide',
    ...binaryNumberOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.MULTIPLICATIVE)
      const v2 = generator.valueToCode(block, 'VAL2', Order.MULTIPLICATIVE)
      return [`${v} / ${v2}`, Order.MULTIPLICATIVE]
    },
    message0: '%1 / %2',
  },
  {
    // For use as a shadow block for blocks that should not accept floating-point numbers.
    opcode: 'math_integer',
    ...numberOutput,
    args0: [{ type: 'field_number', name: 'NUM', value: 0, precision: 1 }],
    generator: (block: Blockly.Block) => [
      block.getFieldValue('NUM'),
      Order.ATOMIC,
    ],
    message0: '%1',
  },
  {
    opcode: 'operator_random',
    ...binaryNumberOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.NONE)
      const v2 = generator.valueToCode(block, 'VAL2', Order.NONE)
      return [`Random.new():NextInteger(${v},${v2})`, Order.HIGH]
    },
    message0: '%{BKY_B_RANDOM}',
  },
  {
    opcode: 'operator_equals',
    ...equalityOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.RELATIONAL)
      const v2 = generator.valueToCode(block, 'VAL2', Order.RELATIONAL)
      return [`${v} == ${v2}`, Order.RELATIONAL]
    },
    message0: '%1 = %2',
  },
  {
    opcode: 'operator_notEquals',
    ...equalityOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.RELATIONAL)
      const v2 = generator.valueToCode(block, 'VAL2', Order.RELATIONAL)
      return [`${v} ~= ${v2}`, Order.RELATIONAL]
    },
    message0: '%1 ~= %2',
  },
  {
    opcode: 'operator_lessThan',
    ...relationalOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.RELATIONAL)
      const v2 = generator.valueToCode(block, 'VAL2', Order.RELATIONAL)
      return [`${v} < ${v2}`, Order.RELATIONAL]
    },
    message0: '%1 < %2',
  },
  {
    opcode: 'operator_greaterThan',
    ...relationalOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.RELATIONAL)
      const v2 = generator.valueToCode(block, 'VAL2', Order.RELATIONAL)
      return [`${v} > ${v2}`, Order.RELATIONAL]
    },
    message0: '%1 > %2',
  },
  {
    opcode: 'operator_not',
    ...booleanOutput,
    args0: [{ type: 'input_value', name: 'VAL', check: 'Boolean' }],
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.UNARY)
      return [`not ${v}`, Order.UNARY]
    },
    message0: '%{BKY_B_NOT}',
  },
  {
    opcode: 'operator_and',
    ...binaryBooleanOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.AND)
      const v2 = generator.valueToCode(block, 'VAL2', Order.AND)
      return [`${v} and ${v2}`, Order.AND]
    },
    message0: '%{BKY_B_AND}',
  },
  {
    opcode: 'operator_or',
    ...binaryBooleanOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.OR)
      const v2 = generator.valueToCode(block, 'VAL2', Order.OR)
      return [`${v} or ${v2}`, Order.OR]
    },
    message0: '%{BKY_B_OR}',
  },
  {
    opcode: 'operator_lessThanOrEqual',
    ...relationalOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.RELATIONAL)
      const v2 = generator.valueToCode(block, 'VAL2', Order.RELATIONAL)
      return [`${v} <= ${v2}`, Order.RELATIONAL]
    },
    message0: '%1 <= %2',
  },
  {
    opcode: 'operator_greaterThanOrEqual',
    ...relationalOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.RELATIONAL)
      const v2 = generator.valueToCode(block, 'VAL2', Order.RELATIONAL)
      return [`${v} >= ${v2}`, Order.RELATIONAL]
    },
    message0: '%1 >= %2',
  },
  {
    opcode: 'operator_mod',
    ...binaryNumberOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.MULTIPLICATIVE)
      const v2 = generator.valueToCode(block, 'VAL2', Order.MULTIPLICATIVE)
      return [`${v} % ${v2}`, Order.MULTIPLICATIVE]
    },
    message0: '%{BKY_B_MOD}',
  },
  {
    opcode: 'operator_concatenate',
    ...binaryOperator,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const v = generator.valueToCode(block, 'VAL', Order.CONCATENATION)
      const v2 = generator.valueToCode(block, 'VAL2', Order.CONCATENATION)
      return [`${v}..${v2}`, Order.CONCATENATION]
    },
    message0: '%{BKY_B_CONCATENATE}',
  },
]
